import padStart from 'lodash-es/padStart';

export enum LogLevel {
    Trace = 1,
    Debug,
    Log,
    Warn,
    Error,
    None
}

const levelColors: { [key in LogLevelStrings]: string } = {
    Trace: 'lightblue',
    Debug: 'blue',
    Log: 'black',
    Warn: 'orange',
    Error: 'red',
    None: 'white'
};

type LogLevelStrings = keyof typeof LogLevel;

const storageKey = 'loggerLevel';

/**
 * on récupère depuis le localStorage la valeur du niveau de log désiré ( persistant à travers les reloads )
 */
export let minLevel: LogLevelStrings = textToLogLevel(localStorage.getItem(storageKey));

/**
 * transforme une string en un niveau de debug acceptable
 */
function textToLogLevel(text: string, defaultLevel: LogLevelStrings = 'Debug'): LogLevelStrings {
    if (Object.keys(levelColors).includes(text)) {
        return text as LogLevelStrings;
    }
    console.warn(`wrong level ${text}, defaults to ${defaultLevel}`);
    return defaultLevel;
}

/**
 * permet de changer le niveau de debug à la volée
 * depuis la console `setLevel('Trace');`
 */
(window as any).setLevel = (level: string) => {
    const logLevel = textToLogLevel(level);
    localStorage.setItem(storageKey, logLevel);
    minLevel = logLevel;
};

export class Logger {

    trace = this.getLogger('Trace');

    debug = this.getLogger('Debug');

    log = this.getLogger('Log');

    warn = this.getLogger('Warn');

    error = this.getLogger('Error');

    groupCollapsed = this.getLogger('Log', 'groupCollapsed');

    constructor(private name) {
    }

    private get time(): string {
        const date = new Date();
        return [date.getHours(), date.getMinutes(), date.getSeconds()].map(a => padStart('' + a, 2, '0')).join(':');
    }

    private getLogger(level: LogLevelStrings, fn: keyof Console = 'log') {
        return (...params: unknown[]) => {
            // should we display this log ?
            if (LogLevel[minLevel] > LogLevel[level]) {
                return () => null;
            }
            // création des styles pour
            const styles = [
                'color: white',
                `background-color: ${levelColors[level]}`,
                'display: inline-block',
                'min-width: 50px',
                'padding: 2px',
                'border-radius: 2px',
                'width: 100px',
                'font-size: 10px'
            ].join(';');
            params = [
                console[fn],
                `%c${level}`,
                styles,
                '[' + this.time + '] [' + this.name + ']',
                ...params
            ];

            return Function.prototype.bind.apply(console[fn], params);
        };
    }
}
